import React from 'react';
import { useEffect, useState, useRef } from 'react';
import axios from '../api/axios';
import { t } from '../i18n';
import { attachmentsHref } from '../utils/attachments';
import {
    MDBCol,
    MDBRow,
    MDBCard,
    MDBCardImage,
    MDBCardBody,
    MDBCardText,
    MDBSwitch,
} from 'mdb-react-ui-kit';

const imgHref = attachmentsHref + 'gutter/';

// CZ only: fallback for the "no gutter system" catalog row (org C001), used only if that row
// is ever missing from /okapy/get - see the auto-select effect below for the normal path.
const NO_GUTTER_SYSTEM_FALLBACK = { model: "Bez okapového systému", barva: "" };

// SK: map covering color → matching gutter color (SK gutter palette: tehlovocervena,
// cervenohneda, tmavohneda, antracitova, cerna)
const gutterColorMapSk = {
    tehlovocervena: "tehlovocervena",
    kralovska_cervena: "tehlovocervena",
    lavova_cervena: "tehlovocervena",
    rezna: "tehlovocervena",

    cervenohneda: "cervenohneda",
    kralovska_bordeaux: "cervenohneda",
    engoba_medena: "cervenohneda",
    antik: "cervenohneda",
    magico: "cervenohneda",

    tmavohneda: "tmavohneda",
    engoba_hneda: "tmavohneda",
    kralovska_mocca: "tmavohneda",
    umbra: "tmavohneda",
    montano: "tmavohneda",
    glazura_tmavohneda: "tmavohneda",

    antracit: "antracitova",
    engoba_antracitova: "antracitova",
    zinkove_seda: "antracitova",
    glazura_seda: "antracitova",
    granit_metalic: "antracitova",
    mocca_metalic: "antracitova",

    cerna: "cerna",
    bridlicove_cerna: "cerna",
    glazura_cerna: "cerna",
    ebenove_cerna: "cerna",
    ebenove_cierna: "cerna",
};

// CZ: map covering color → matching gutter color (CZ gutter palette only has 4 colors:
// cerna, cervena, hneda, antracitova - narrower than SK's, so red-brown and dark-brown
// coverings both collapse into "hneda")
const gutterColorMapCz = {
    cerna: "cerna",
    bridlicove_cerna: "cerna",
    ebenove_cerna: "cerna",
    glazura_cerna: "cerna",
    glazura_mangan_cerna: "cerna",

    cihlove_cervena: "cervena",
    pamatkove_cervena: "cervena",
    rezna: "cervena",
    engoba_medena: "cervena",

    cervenohneda: "hneda",
    "cerveno-hneda": "hneda",
    glazura_cervenohneda: "hneda",
    engoba_hneda: "hneda",
    glazura_kastanove_hneda: "hneda",
    glazura_mangan_tmavohneda: "hneda",
    glazura_tmavohneda: "hneda",
    tmavohneda: "hneda",
    "mocca-metalic": "hneda",

    antracit: "antracitova",
    engoba_antracitova: "antracitova",
    engoba_mangan_antracitova: "antracitova",
    glazura_seda: "antracitova",
    glazura_tmave_seda: "antracitova",
    zinkove_seda: "antracitova",
    "granit-metalic": "antracitova",
};


function Gutters(props) {
    const [gutters, setGutters] = useState();
    const [chosenOkap, setChosenOkap] = useState();
    const didMount = useRef(false);
    const didLoad = useRef(false);

    // CZ only: model selector state
    const [gutterCalc, setGutterCalc] = useState(true);
    const [chosenModel, setChosenModel] = useState("Stabicor M");

    useEffect(() => {
        if (didLoad.current) return;
        let isMounted = true;
        const controller = new AbortController();

        const getOkapy = async () => {
            try {
                const response = await axios.get('/okapy/get', {
                    signal: controller.signal
                });
                didLoad.current = true;
                isMounted && setGutters(response.data);
                setChosenOkap(response.data.find(okap => okap.model === "Stabicor M" && okap.barva === "cerna"));
            } catch (err) {
                //
            }
        }

        getOkapy();

        return () => {
            isMounted = false;
            controller.abort();
        }
    }, []);

    useEffect(() => {
        if (!didMount.current) {
            didMount.current = true;
            return;
        }
        props.step.data = { chosenOkap, gutterCalc };
    }, [chosenOkap, gutterCalc]);

    // auto-select gutter color to match covering color
    useEffect(() => {
        if (!gutters) return;

        // CZ: calc switched off - no gutter system is being ordered, nothing to match.
        // "No system" is a real catalog row (org C001) identified structurally by its empty
        // barva, not by matching translated text - the literal below is only a fallback for
        // the unlikely case that row is ever missing from the catalog.
        if (process.env.REACT_APP_LANG !== 'sk' && !gutterCalc) {
            setChosenOkap(gutters.find(okap => okap.barva === "") ?? NO_GUTTER_SYSTEM_FALLBACK);
            return;
        }

        // no covering selected (or deselected) - leave whatever gutter is currently chosen as-is
        if (!props.selectedCoveringColor) return;

        var langGutterColorMap = process.env.REACT_APP_LANG === 'sk' ? gutterColorMapSk : gutterColorMapCz;
        var selectedGutterColor = langGutterColorMap[props.selectedCoveringColor] || "cerna";
        var model = process.env.REACT_APP_LANG === 'sk' ? "Stabicor M" : chosenModel;
        setChosenOkap(gutters.find(okap => okap.model === model && okap.barva === selectedGutterColor) ?? null);
    }, [props.selectedCoveringColor, gutters, chosenModel, gutterCalc]);

    if (process.env.REACT_APP_LANG === 'sk') {
        // SK: only Stabicor M, color picker only
        const guttersColors = gutters?.filter(okap => okap.model === "Stabicor M");
        return (
            <MDBRow className='mt-3'>
                {guttersColors?.map(color => {
                    const selectedMark = (chosenOkap && chosenOkap.barva === color.barva) ? "" : "d-none"
                    return (
                        <MDBCol size='4' lg='2' key={color.modelNazev + color.barvaNazev}>
                            <MDBCard className='h-100' background='light' alignment='center'
                                onClick={() => { setChosenOkap(color); }}>
                                <i className={"fas fa-check-circle selected-covering-icon " + selectedMark}></i>
                                <MDBCardImage
                                    src={imgHref + "/okapy-vicko-" + color.barva + ".png"}
                                    alt='&empty;'
                                    position='top'
                                    className='p-1'
                                />
                                <MDBCardBody className='p-1 mb-2'>
                                    <MDBCardText>{color.barvaNazev}</MDBCardText>
                                </MDBCardBody>
                            </MDBCard>
                        </MDBCol>
                    )
                })}
            </MDBRow>
        );
    }

    // CZ: multi-model selector + model-specific color picker + gutterCalc toggle
    let guttersModels = [];
    gutters?.forEach(okap => {
        if (!guttersModels.some(o => o.model === okap.model)) {
            if (okap.barva === "") { return; }
            guttersModels.push(okap);
        }
    })
    guttersModels.sort((a, b) => a.modelPoradi - b.modelPoradi);

    let guttersColors = [];
    gutters?.forEach(okap => {
        if (okap.model === chosenModel) {
            guttersColors.push(okap);
        }
    })
    guttersColors?.sort((a, b) => a.barvaPoradi - b.barvaPoradi);

    return (
        <div>
            <MDBSwitch id='gutterCalculation' name='gutterCalculation'
                wrapperClass="d-flex align-items-center" defaultChecked={gutterCalc}
                label={t("gutter_switch")} labelClass='fs-5'
                onClick={(e) => { setGutterCalc(!gutterCalc) }} />

            {gutterCalc && (
                <MDBRow className='mt-3'>
                    <MDBCol md='12' lg='5' className='mb-4'>
                        <div className="d-grid">
                            <MDBRow>
                                {guttersModels?.map(okap => {
                                    const selectedMark = (chosenModel === okap.modelNazev) ? "" : "d-none"
                                    return (
                                        <MDBCol size='6' lg='6' key={okap.typ + okap.model + okap.barva}>
                                            <MDBCard className='h-100' background='light' alignment='center'
                                                onClick={() => {
                                                    setChosenModel(okap.model);
                                                    setChosenOkap(okap);
                                                }}>
                                                <i className={"fas fa-check-circle selected-covering-icon " + selectedMark}></i>
                                                <MDBCardImage
                                                    src={imgHref + okap.model + ".png"}
                                                    alt='&empty;'
                                                    position='top'
                                                    className='p-1 pt-3'
                                                />
                                                <MDBCardBody className='p-1 mb-2'>
                                                    <MDBCardText>{okap.modelNazev}</MDBCardText>
                                                </MDBCardBody>
                                            </MDBCard>
                                        </MDBCol>
                                    )
                                })}
                            </MDBRow>
                        </div>
                    </MDBCol>
                    <MDBCol md='12' lg='6' offsetLg='1'>
                        {guttersColors?.length ? (
                            <MDBRow>
                                <h5 className='text-center mb-3'>{t("gutter_color_label")}</h5>
                                {guttersColors?.map(color => {
                                    const selectedMark = (chosenOkap && chosenOkap.barva === color.barva) ? "" : "d-none"
                                    return (
                                        <MDBCol size='3' lg='3' key={color.modelNazev + color.barvaNazev}>
                                            <MDBCard className='h-100' background='light' alignment='center'
                                                onClick={() => { setChosenOkap(color); }}>
                                                <i className={"fas fa-check-circle selected-covering-icon " + selectedMark}></i>
                                                <MDBCardImage
                                                    src={imgHref + "/okapy-vicko-" + color.barva + ".png"}
                                                    alt='&empty;'
                                                    position='top'
                                                    className='p-1'
                                                />
                                                <MDBCardBody className='p-1 mb-2'>
                                                    <MDBCardText>{color.barvaNazev}</MDBCardText>
                                                </MDBCardBody>
                                            </MDBCard>
                                        </MDBCol>
                                    )
                                })}
                            </MDBRow>
                        ) : <div></div>}
                    </MDBCol>
                </MDBRow>
            )}
        </div>
    );

}

export default Gutters;
